<script setup>
import AppLayout from '@/Layouts/AppLayout.vue'
import {
    MagnifyingGlassIcon,
    XMarkIcon,
    BellAlertIcon,
    ArrowRightIcon,
    AdjustmentsHorizontalIcon,
} from '@heroicons/vue/24/outline'
import { ref, watch } from 'vue'
import debounce from 'lodash/debounce'
import { router } from '@inertiajs/vue3'
import BusinessCard from '@/Pages/User/Businesses/Partials/BusinessCard.vue'
import Pagination from '@/Components/Pagination.vue'

const props = defineProps({
    categories: {
        type: Object,
        required: true,
    },

    businesses: {
        type: Object,
        required: true,
    },

    category: {
        type: Object,
        required: true,
    },

    filters: {
        type: Object,
        required: false,
    },
})

const search = ref(props.filters?.search)
const selected_childrens = ref(props.filters?.podkategorie ?? [])

const selectChildren = (children) => {
    if (selected_childrens.value.includes(children)) {
        selected_childrens.value = selected_childrens.value.filter((item) => item !== children)
    } else {
        selected_childrens.value.push(children)
    }

    getBusinesses()
}

watch([search, selected_childrens], () => {
    getBusinesses()
})

const getBusinesses = debounce(() => {
    router.get(
        route('businesses.category-index', props.category.slug),
        {
            search: search.value,
            podkategorie: selected_childrens.value,
        },
        {
            preserveState: true,
            preserveScroll: true,
        }
    )
}, 500)

const filter_advanced_dropdown = ref(false)

const clearFilters = () => {
    search.value = ''
    selected_childrens.value = []
    filter_advanced_dropdown.value = false
    getBusinesses()
}
</script>

<template>
    <AppLayout
        :title="category.name"
        :description="category.description"
    >
        <template #content>
            <section class="relative pt-10 lg:pt-48 xl:px-4 2xl:px-0">
                <div class="absolute inset-0 -z-20 flex h-[40rem] w-full">
                    <img
                        class="relative w-full object-cover opacity-50"
                        :src="'/storage/categories/' + category.icon"
                        :alt="category.name"
                    />

                    <div class="absolute inset-0 bg-gradient-to-b from-transparent to-white" />
                </div>

                <div class="py-20">
                    <div class="flex flex-col items-center space-y-10">
                        <img
                            class="h-48 w-48 rounded-full bg-white object-cover object-center p-2 shadow-lg"
                            :src="'/storage/categories/' + category.icon"
                            :alt="category.name"
                        />

                        <h1
                            class="font-caveat relative mb-10 text-center text-6xl leading-10 font-semibold tracking-wider capitalize"
                        >
                            {{ category.name }}
                        </h1>
                    </div>
                </div>

                <div class="container mx-auto px-4 lg:px-20 xl:px-0">
                    <div class="relative">
                        <div class="grid grid-cols-12 md:gap-6">
                            <div class="col-span-12 mb-2 flex flex-row space-x-4 xl:col-span-9 xl:mb-0 2xl:col-span-10">
                                <div class="flex h-full items-center">
                                    <button
                                        :class="
                                            {
                                                true: 'bg-stone-800',
                                                false: 'rotate-0',
                                            }[filter_advanced_dropdown]
                                        "
                                        class="faster-animation rounded-lg px-2 py-1 hover:bg-stone-100 lg:px-4 lg:py-2"
                                        @click="filter_advanced_dropdown = !filter_advanced_dropdown"
                                    >
                                        <component
                                            class="h-7 w-7"
                                            :class="
                                                {
                                                    true: 'stroke-stone-200',
                                                    false: 'stroke-stone-800',
                                                }[filter_advanced_dropdown]
                                            "
                                            :is="
                                                {
                                                    true: XMarkIcon,
                                                    false: AdjustmentsHorizontalIcon,
                                                }[filter_advanced_dropdown]
                                            "
                                        />
                                    </button>
                                </div>

                                <div
                                    id="category--scrollbar"
                                    class="mb-5 flex h-full items-center space-x-4 overflow-x-auto pb-2 whitespace-nowrap lg:pb-0"
                                >
                                    <Link
                                        :href="route('businesses.index')"
                                        class="faster-animation rounded-lg px-4 py-2 text-gray-600 hover:bg-stone-100"
                                    >
                                        Vše
                                    </Link>

                                    <Link
                                        v-for="un_category in categories"
                                        :key="un_category.id"
                                        :href="route('businesses.category-index', un_category.slug)"
                                        class="faster-animation rounded-lg px-4 py-2 text-gray-600"
                                        :class="
                                            {
                                                true: 'bg-stone-800 text-white',
                                                false: 'bg-transparent hover:bg-stone-100',
                                            }[category.slug === un_category.slug]
                                        "
                                    >
                                        {{ un_category.name }}
                                    </Link>
                                </div>
                            </div>

                            <div class="col-span-12 grid w-full xl:col-span-3 2xl:col-span-2">
                                <div class="flex items-center">
                                    <p
                                        class="flex rounded-l-lg border border-r-0 border-gray-200 bg-gray-100 px-3 py-2.5 text-gray-500"
                                    >
                                        <MagnifyingGlassIcon class="h-6 stroke-gray-600" />
                                    </p>

                                    <input
                                        v-model="search"
                                        type="text"
                                        placeholder="Název podniku ..."
                                        class="focus:ring-opacity-40 block w-full rounded-lg rounded-l-none border border-gray-200 bg-white px-5 py-2.5 text-gray-700 placeholder-gray-400/70 focus:border-red-400 focus:ring focus:ring-red-300 focus:outline-none"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="mt-5">
                        <Transition
                            enter-active-class="ease-out duration-300"
                            enter-from-class="opacity-0"
                            enter-to-class="opacity-100"
                            leave-active-class="ease-in duration-300"
                            leave-from-class="opacity-100"
                            leave-to-class="opacity-0"
                        >
                            <div
                                v-if="filter_advanced_dropdown"
                                class="relative rounded-xl bg-stone-800 p-10"
                            >
                                <div>
                                    <h2 class="font-roboto-slab text-xl font-semibold text-gray-200 uppercase">
                                        Podkategorie
                                    </h2>

                                    <div class="mt-4">
                                        <button
                                            v-for="children in category.relationships.childrens"
                                            :key="children.id"
                                            @click="selectChildren(children.slug)"
                                            class="mr-1 mb-1 inline-flex cursor-pointer items-center space-x-2 rounded-md border-2 px-4 py-2 hover:border-red-500"
                                            :class="
                                                {
                                                    true: 'border-red-500 bg-stone-500 text-white',
                                                    false: 'border-stone-600 bg-stone-700 text-zinc-300',
                                                }[selected_childrens.includes(children.slug)]
                                            "
                                        >
                                            <span class="text-xs font-bold">
                                                {{ children.name }}
                                            </span>
                                        </button>
                                    </div>
                                </div>

                                <hr class="mt-5 border-stone-600" />

                                <div class="mt-5">
                                    <button
                                        @click="clearFilters"
                                        class="slower-animation group flex cursor-pointer items-center space-x-2 rounded-md border border-stone-500 bg-stone-700 px-6 py-3 hover:bg-white"
                                    >
                                        <XMarkIcon
                                            class="faster-animation h-4 w-4 text-stone-200 group-hover:text-stone-800"
                                        />

                                        <span
                                            class="faster-animation text-xs font-bold text-stone-200 group-hover:text-stone-800"
                                        >
                                            Vyčistit filtry
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </Transition>

                        <main>
                            <div v-if="businesses?.data?.length > 0">
                                <div
                                    class="mt-10 grid grid-cols-1 gap-2 lg:grid-cols-2 lg:gap-4 xl:grid-cols-4 xl:gap-6"
                                >
                                    <BusinessCard :businesses="businesses.data" />
                                </div>

                                <Pagination :pages="businesses.meta" />
                            </div>

                            <div
                                v-else
                                class="mt-40 flex items-start justify-center"
                            >
                                <div class="flex flex-col items-center space-y-5">
                                    <div class="rounded-xl bg-red-100 p-2">
                                        <BellAlertIcon class="h-12 w-12 text-red-400" />
                                    </div>

                                    <h2 class="text-2xl font-semibold text-gray-700">
                                        Nebyly nalezené žádné příspěvky
                                    </h2>

                                    <div
                                        class="slower-animation relative top-5 rounded-md border border-gray-200 bg-gray-50 hover:border-red-300"
                                    >
                                        <Link
                                            aria-label="Přejít na seznam podniků"
                                            :href="route('businesses.index')"
                                            class="group inline-flex items-center py-2 pr-8 pl-4 text-base font-semibold text-red-400"
                                        >
                                            Zpět na seznam podniků

                                            <ArrowRightIcon
                                                class="faster-animation relative left-2 h-5 w-5 group-hover:left-4"
                                            />
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </main>
                    </div>
                </div>
            </section>
        </template>
    </AppLayout>
</template>
